import axios, {
  AxiosError,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';
import { message } from 'antd';

import { getAccessToken, getRefreshToken, useAuthStore } from '@/stores/auth';
import { currentLoginSession } from '@/stores/loginSession';
import { revalidatePermissions } from '@/stores/permissions';

/**
 * 后端统一响应包装（对齐 common-core 的 ApiResponse<T>）
 */
export interface ApiResponse<T = unknown> {
  code: number;
  message: string;
  data: T;
  timestamp?: number;
  trace_id?: string;
  traceId?: string;
}

declare module 'axios' {
  export interface AxiosRequestConfig {
    /**
     * 跳过响应拦截器里的全局 message.error。
     * 用于「输入过程中的预期失败」应在面板内消化的请求（如模板实时预览），
     * 避免防抖打字时每 500ms 弹一次 toast。鉴权跳转等副作用不受影响。
     *
     * 订阅相关的读取请求（订阅计划、设备订阅、办理、设备权益、AI 调用待处理）
     * 一律带上它：这些页面各自有页内提示，同一件事只提示一次（见 P8）。
     * 调用页还没有页内提示的读取（如 eSIM 用量）暂不带，免得失败一次都不提示。
     * 写请求保持各自的现有行为，不在这里统一开关。
     */
    skipErrorToast?: boolean;
  }
}

type RequestConfig = InternalAxiosRequestConfig & {
  _retry?: boolean;
  skipErrorToast?: boolean;
  /**
   * 发出请求时的登录会话身份（见 stores/loginSession，未登录为 null）。由公共请求入口在创建请求的同步调用中记下；
   * 401 后的刷新重放会再次经过请求拦截器，沿用第一次记下的会话，不重新取。
   */
  _sentAs?: string | null;
};

/** 当前登录会话身份（见 {@link currentLoginSession}）；请求、刷新与重放的边界都按它判断。 */
function currentIdentity(): string | null {
  return currentLoginSession();
}

/** 请求在等待期间登录会话变了，页面没有用新的登录会话重新发送它时给调用方的说明。 */
export const IDENTITY_CHANGED_MESSAGE =
  '登录会话已变化，页面没有用新的登录会话重新发送这次请求；这次发送收到 401 或尚未发出，服务端没有处理它';

/**
 * 登录会话在请求等待期间变了（登出、换人，或同一账号重新登录），请求没有用新的登录会话发出或重新发送。
 *
 * <p>产生的地方：请求拦截器每次经过（首次发出、无需刷新、401 后重放）发现记下的会话已不是当前会话——
 * 动用当前会话的任何令牌之前核对一次，主动刷新等待之后再核对一次；401 后准备刷新并重放时会话已变；
 * 刷新令牌期间、或刷新失败交到调用方之前会话变了（成功、网络失败或业务失败都一样）。
 *
 * <p>“没有重新发送”本身不能说明第一次发送没有执行；能说明它没有被服务端处理，是因为以上每一处的这次发送要么
 * 还没有发出（主动刷新路径），要么收到了 401（被动刷新与重放路径）。调用方据此把它当作“没有发出”，
 * 需要当前登录的管理员重新、明确地发起，不能当成“结果未知”。产生这个错误时不弹提示、不清会话、不跳转登录页，
 * 新会话保持不变。
 *
 * <p>{@link IdentityChangedError#code} 与 axios 错误码同一形态，调用方可按错误码识别（测试里常整体替换本模块）。
 */
export class IdentityChangedError extends Error {
  readonly code = 'ERR_IDENTITY_CHANGED';
  /** 没有重新发送的请求地址 */
  readonly url?: string;

  constructor(url?: string) {
    super(IDENTITY_CHANGED_MESSAGE);
    this.name = 'IdentityChangedError';
    this.url = url;
  }
}

/**
 * 发出请求的登录会话是否已不是当前会话。发出时未登录（没有会话）的请求不在此列，仍按原逻辑处理；
 * 发出时已登录、此刻已登出或已重新登录（含同一账号），都算会话变化。
 */
function identityChanged(config: RequestConfig): boolean {
  const sentAs = config._sentAs;
  return sentAs !== undefined && sentAs !== null && sentAs !== currentIdentity();
}

function shouldSkipErrorToast(config?: { skipErrorToast?: boolean }): boolean {
  return !!config?.skipErrorToast;
}

/**
 * 运营能看懂的两句失败说明（P2）：报错只说发生了什么，不带业务码、字段路径、原因编码与英文原文。
 *
 * 后端只回了英文原文（System error / Access Denied），或前端能识别的 401 / 403 / 5xx 时，
 * 一律换成这两句。后端自己的错误码与文案不动，这里只做前端映射。
 */
export const PERMISSION_DENIED_MESSAGE = '你没有该操作的权限';
export const SERVICE_BUSY_MESSAGE = '系统繁忙，请稍后再试';

/**
 * 把一次失败的服务端说明收成运营可读的一句。
 *
 * 服务端已经给出的中文业务说明原样保留（那是这次拒绝的具体原因）；只把能识别的
 * 权限、服务故障与英文原文换成上面两句。返回空串表示服务端没有给出说明，由调用方决定再说什么。
 */
export function operatorErrorMessage(message: unknown, httpStatus?: number): string {
  const text = typeof message === 'string' ? message.trim() : '';
  if (httpStatus === 401 || httpStatus === 403 || /access denied/i.test(text)) {
    return PERMISSION_DENIED_MESSAGE;
  }
  if ((httpStatus != null && (httpStatus >= 500 || httpStatus === 408)) || /system error/i.test(text)) {
    return SERVICE_BUSY_MESSAGE;
  }
  return text;
}

const axiosClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE || '/',
  timeout: 15_000,
  // 数组参数必须重复同名键（groupBy=USER&groupBy=DEVICE）。axios 默认加方括号发
  // groupBy[]=USER，而 Spring 按 groupBy 这个名字取参数，groupBy[] 匹配不上，
  // 绑定结果是 null —— 不报错、不告警。/estimates/rankings 因此恒报「至少需要一个
  // 查看维度」，而 /estimates/summaries 更糟：静默返回不分组的汇总，数字看着正常。
  // indexes: null 即重复键形态，与 downloadEstimates 手拼 URLSearchParams 的写法一致。
  paramsSerializer: { indexes: null },
});

/**
 * Axios 的请求拦截器可能异步执行，不能到拦截器里才绑定身份。
 * 所有公共发送入口在调用栈内复制配置并固定登录会话；重放配置已带原会话时保留它。
 * get/post 等 Axios 别名绑定了内部 context，需在各别名入口绑定，不能只改实例的 request 属性。
 */
function bindRequestSession(args: unknown[], configIndex: number): unknown[] {
  const bound = [...args];
  const config = (bound[configIndex] ?? {}) as AxiosRequestConfig & Pick<RequestConfig, '_sentAs'>;
  bound[configIndex] = { ...config, _sentAs: '_sentAs' in config ? config._sentAs : currentIdentity() };
  return bound;
}

for (const method of ['request', 'get', 'delete', 'head', 'options', 'post', 'put', 'patch', 'postForm', 'putForm', 'patchForm']) {
  const send = Reflect.get(axiosClient, method) as (...args: unknown[]) => unknown;
  Object.defineProperty(axiosClient, method, {
    configurable: true,
    writable: true,
    value: (...args: unknown[]) => {
      const configIndex = method === 'request' ? (typeof args[0] === 'string' ? 1 : 0)
        : ['get', 'delete', 'head', 'options'].includes(method) ? 1 : 2;
      return Reflect.apply(send, axiosClient, bindRequestSession(args, configIndex));
    },
  });
}

/** 同时覆盖 Axios 实例自身可调用的 http(config) / http(url, config) 入口。 */
export const http = new Proxy(axiosClient, {
  apply: (send, context, args) => Reflect.apply(send, context,
    bindRequestSession(args, typeof args[0] === 'string' ? 1 : 0)),
});

http.interceptors.request.use(async (config) => {
  // 登录/刷新请求绝不带 Authorization：带上过期的旧 token 会被网关校验后 401，导致登录都进不去。
  if (isAuthEndpoint(config.url)) return config;

  // 公共入口已同步绑定登录会话；401 重放保留原会话，拦截器只核对，不读取新会话替换它。
  const request = config as RequestConfig;

  // 每一次经过（首次发出、无需刷新、401 后重放）都先核对：记下的登录会话已不是当前登录会话时不发出，也不动用当前会话的
  // 任何令牌（包括拿它的刷新令牌去刷新）——不能把上一位的请求带着新登录会话的令牌发出去。
  // 发出时未登录（没有登录会话）的请求不在此列，按原逻辑处理。
  if (identityChanged(request)) throw new IdentityChangedError(config.url);

  // access token 本地已知过期 → 先主动刷新，避免打出一个跨域 401。
  //（网关的 4xx 错误响应常缺 CORS 头，浏览器读不到，会变成不可跳转的 "Network Error"。）
  if (isAccessTokenExpired() && getRefreshToken()) {
    const refresh = refreshCurrentSession();
    try {
      await refresh.promise;
    } catch (e) {
      // 刷新失败的副作用（清会话、提示、跳转）只作用于这次刷新所属的登录会话：失败交到这里时已不是他（换人、登出或重新登录），
      // 不碰新会话，请求不发出
      if (e instanceof IdentityChangedError) throw e;
      if (currentIdentity() !== refresh.identity) throw new IdentityChangedError(config.url);
      redirectToLogin();
      throw new axios.Cancel('session expired');
    }
    // 刷新等待期间换了登录会话：不能用新登录会话的令牌发出上一位的请求
    if (identityChanged(request)) throw new IdentityChangedError(config.url);
  }

  const token = getAccessToken();
  if (token) {
    config.headers = config.headers ?? {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

interface AdminTokenResponseRaw {
  username: string;
  role: string;
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
  display_name?: string;
}

const REFRESH_URL = '/iam/admin/auth/refresh';

/**
 * 用当前会话的刷新令牌换新令牌，结果只写回这次刷新所属的登录会话（发起时的当前登录会话，也就是刷新令牌的主人）。
 *
 * <p>等回来时登录会话已变（换人、登出或重新登录）：结果不写回，交回 {@link IdentityChangedError}，免得覆盖新登录会话的会话。
 * 失败（网络或业务）原样抛出；失败的副作用（清会话、提示、跳转）由调用方按“失败交到手里时是否仍是这次刷新所属的登录会话”决定。
 */
async function refreshAccessToken(identity: string | null): Promise<string> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) throw new Error('no refresh token');
  const resp = await axios.post<ApiResponse<AdminTokenResponseRaw>>(
    REFRESH_URL,
    // 放在提交内容里：查询参数会被负载均衡器访问日志整串记录
    { refresh_token: refreshToken },
    {
      baseURL: http.defaults.baseURL,
      timeout: http.defaults.timeout as number,
    },
  );
  const body = resp.data;
  if (!body || (body.code !== 0 && body.code !== 200)) {
    throw new Error(body?.message || 'refresh failed');
  }
  const data = body.data;
  if (currentIdentity() !== identity) throw new IdentityChangedError(REFRESH_URL);
  // 刷新令牌不是新的登录：沿用当前登录会话编号，同会话里等待中的请求照常重放
  useAuthStore.getState().refreshSession({
    accessToken: data.access_token,
    refreshToken: data.refresh_token,
    tokenType: data.token_type,
    expiresIn: data.expires_in,
    user: {
      username: data.username,
      displayName: data.display_name,
      role: data.role,
    },
  });
  return data.access_token;
}

/** 一次刷新：所属身份与结果。 */
interface Refresh {
  /** 这次刷新所属的身份：发起时的当前身份，也就是所用刷新令牌的主人 */
  identity: string | null;
  promise: Promise<string>;
}

/**
 * 正在进行的刷新。并发去重只在同一登录会话内生效：同一登录会话的多个请求同时发现令牌过期时只刷新一次；
 * 另一登录会话的请求不等待它、也不承接它的成败，而是为自己另起一次刷新。
 */
let refreshing: Refresh | null = null;

/**
 * 为当前登录会话刷新令牌，同一登录会话进行中的刷新直接共用。
 * 返回这次刷新及其所属登录会话，调用方据此判断刷新失败时清会话、提示、跳转该不该发生。
 */
function refreshCurrentSession(): Refresh {
  const identity = currentIdentity();
  if (refreshing && refreshing.identity === identity) return refreshing;
  const promise: Promise<string> = refreshAccessToken(identity).finally(() => {
    if (refreshing?.promise === promise) refreshing = null;
  });
  const refresh: Refresh = { identity, promise };
  refreshing = refresh;
  return refresh;
}

const EXPIRY_SKEW_MS = 5_000; // 提前 5s 视为过期，避开临界抖动
function isAccessTokenExpired(): boolean {
  const { accessToken, expiresAt } = useAuthStore.getState();
  // 无 token = 匿名，不算“过期”；无 expiresAt = 不知道有效期，交给服务端判。
  if (!accessToken || !expiresAt) return false;
  return Date.now() > expiresAt - EXPIRY_SKEW_MS;
}

function isAuthEndpoint(url?: string): boolean {
  if (!url) return false;
  return isLoginEndpoint(url) || url.includes('/iam/admin/auth/refresh');
}

/**
 * 登录接口本身。它的 401 语义是「这次凭据没通过」（密码错 / 账号锁定 / 限流），
 * 与「会话过期」完全不同，必须区别对待——见响应拦截器。
 */
function isLoginEndpoint(url?: string): boolean {
  return !!url && url.includes('/iam/admin/auth/login');
}

/**
 * iam-service ErrorCode.AUTH_CREDENTIALS_INVALID：用户名或密码错误。
 * 后端按 (code/100)%10=2 归成认证类错误 → HTTP 401，但响应体里带着这个业务码，
 * 用它再挡一层，可以在 URL 判据之外避免误判。
 * 注意：用户名不存在与密码错误共用此码（防用户名枚举），文案不要细分。
 */
const AUTH_CREDENTIALS_INVALID = 10201;

function redirectToLogin() {
  useAuthStore.getState().clear();
  message.error('登录已过期，请重新登录');
  if (!location.pathname.startsWith('/login')) {
    location.href = `/login?redirect=${encodeURIComponent(location.pathname)}`;
  }
}

http.interceptors.response.use(
  (resp: AxiosResponse<ApiResponse>) => {
    const body = resp.data;
    if (body && typeof body === 'object' && 'code' in body) {
      if (body.code !== 0 && body.code !== 200) {
        if (!shouldSkipErrorToast(resp.config)) {
          message.error(operatorErrorMessage(body.message) || '请求失败');
        }
        return Promise.reject(body);
      }
    }
    return resp;
  },
  async (error: AxiosError<ApiResponse>) => {
    // 已判定为“登录会话已变化、没有重新发送”的请求：原样交回调用方，不弹提示、不清会话、不跳转
    if (error instanceof IdentityChangedError) return Promise.reject(error);

    const status = error.response?.status;
    const config = error.config as RequestConfig | undefined;

    // 凭据校验失败的 401 不是会话过期：不能清会话、不能跳转，更不能把后端的真实原因
    // （用户名或密码错误 / 账号已锁定 / 尝试过于频繁）盖成"登录已过期，请重新登录"——
    // 否则密码输错后即使再输对，用户看到的仍是那句误导性的提示。
    // 双判据：登录接口 或 认证失败的专用业务码（见上方常量）。
    if (
      status === 401 &&
      (isLoginEndpoint(config?.url) ||
        error.response?.data?.code === AUTH_CREDENTIALS_INVALID)
    ) {
      if (!shouldSkipErrorToast(config)) {
        message.error(error.response?.data?.message || '用户名或密码错误');
      }
      return Promise.reject(error);
    }

    // 401 时原请求没有被处理。发出它的登录会话已不是当前登录会话（登出或换人）时，不刷新、不重放、不跳转：
    // 用新登录会话的令牌重放会让上一位的写操作以新登录会话生效。发出时未登录的请求不在此列，按下面的原逻辑处理。
    if (status === 401 && config && !isAuthEndpoint(config.url) && identityChanged(config)) {
      return Promise.reject(new IdentityChangedError(config.url));
    }

    if (
      status === 401 &&
      config &&
      !config._retry &&
      !isAuthEndpoint(config.url) &&
      getRefreshToken()
    ) {
      config._retry = true;
      const refresh = refreshCurrentSession();
      try {
        const newToken = await refresh.promise;
        // 刷新令牌期间换了登录会话：同样不重放（重放进入请求拦截器时还会再核对一次）
        if (identityChanged(config)) return Promise.reject(new IdentityChangedError(config.url));
        config.headers = config.headers ?? {};
        config.headers.Authorization = `Bearer ${newToken}`;
        return http.request(config);
      } catch (e) {
        // 刷新失败的副作用（清会话、提示、跳转）只作用于这次刷新所属的登录会话：失败交到这里时已不是他（换人、登出或重新登录），不碰新会话
        if (e instanceof IdentityChangedError) return Promise.reject(e);
        if (currentIdentity() !== refresh.identity) return Promise.reject(new IdentityChangedError(config.url));
        redirectToLogin();
        return Promise.reject(e);
      }
    }

    if (status === 401) {
      redirectToLogin();
      return Promise.reject(error);
    }

    // 403 = 前端以为有这个权限、后端拒绝 → 本地权限集已经跟不上后端了
    //（典型场景：管理员刚改完角色授权，而该用户的页面还停在登录那一刻的权限上）。
    // 顺手重新对齐一次 /me，让菜单和按钮立刻回到真实状态；节流在注册方，不会打爆。
    // 排除 /me 自身，否则它自己 403 时会自我触发。
    if (status === 403 && !config?.url?.includes('/admin/rbac/me')) {
      revalidatePermissions();
    }

    // 无响应（网络错误，或跨域 401 被浏览器拦成不可读的 opaque 失败）：
    // 若本地已知会话已过期，按登录过期跳转，而不是只弹 "Network Error" 卡住不动。
    // 发出请求的登录会话已不是当前登录会话时不跳转：那是上一位的请求，不能因此清掉新登录会话。
    if (
      !error.response &&
      !isAuthEndpoint(config?.url) &&
      !(config && identityChanged(config)) &&
      isAccessTokenExpired()
    ) {
      redirectToLogin();
      return Promise.reject(error);
    }

    if (!shouldSkipErrorToast(config)) {
      // 没有响应（超时、断网）时 axios 的说明是英文原文，服务端也没给出可展示的说明，
      // 统一按「系统繁忙」说；有响应时按状态与服务端说明映射，不给运营看业务码或英文原文。
      message.error(
        error.response
          ? operatorErrorMessage(error.response.data?.message, status) || SERVICE_BUSY_MESSAGE
          : SERVICE_BUSY_MESSAGE,
      );
    }
    return Promise.reject(error);
  },
);

export async function request<T = unknown>(config: AxiosRequestConfig): Promise<T> {
  const resp = await http.request<ApiResponse<T>>(config);
  return resp.data.data as T;
}

export interface ResponseMeta<T> {
  data: T;
  etag?: string;
}

/** 需要乐观并发控制的资源使用此入口，业务数据与响应元数据同时返回。 */
export async function requestWithMeta<T = unknown>(
  config: AxiosRequestConfig,
): Promise<ResponseMeta<T>> {
  const resp = await http.request<ApiResponse<T>>(config);
  const rawEtag = resp.headers.etag as string | undefined;
  return {
    data: resp.data.data as T,
    etag: rawEtag?.startsWith('W/') ? rawEtag.slice(2) : rawEtag,
  };
}
